import { fetchLandingDomain, patchLandingDomain, checkLandingDomain } from '@/api/landings';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import type { ILandingDomain } from '@/entities/landing.entity';
import { PageScroll } from '@/layouts/page-layout';
import { formatApiError } from '@/lib/format-api-error';
import CopyableUrlRow from '@/pages/landings/components/copyable-url-row';
import DeactivateDomainDialog from '@/pages/landings/components/deactivate-domain-dialog';
import LandingsGate from '@/pages/landings/landings-gate';
import LandingDomainStatusBadge from '@/pages/landings/landing-domain-status-badge';
import axios from 'axios';
import { GlobeIcon, RefreshCwIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

export default function LandingDomainDetailPage() {
	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<LandingsGate>
				<DomainDetailContent />
			</LandingsGate>
		</PageScroll>
	);
}

function DomainDetailContent() {
	const { id } = useParams<{ id: string }>();
	const domainId = Number(id);

	const [domain, setDomain] = useState<ILandingDomain | null>(null);
	const [loading, setLoading] = useState(true);
	const [patching, setPatching] = useState(false);
	const [isChecking, setIsChecking] = useState(false);
	const [deactivateOpen, setDeactivateOpen] = useState(false);

	const loadDomain = useCallback(async () => {
		if (!Number.isFinite(domainId)) return;
		setLoading(true);
		try {
			const data = await fetchLandingDomain(domainId);
			setDomain(data);
		} catch {
			setDomain(null);
		} finally {
			setLoading(false);
		}
	}, [domainId]);

	useEffect(() => {
		void loadDomain();
	}, [loadDomain]);

	function copyText(text: string) {
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	async function handleDeactivate() {
		if (!domain) return;
		setPatching(true);
		try {
			const updated = await patchLandingDomain(domain.id, 'disabled');
			setDomain(updated);
			setDeactivateOpen(false);
			toast.success('Домен отключён');
		} finally {
			setPatching(false);
		}
	}

	async function handleEnable() {
		if (!domain) return;
		setPatching(true);
		try {
			const updated = await patchLandingDomain(domain.id, 'online', { suppressToast: true });
			setDomain(updated);
			if (updated.status === 'online') {
				toast.success('Домен включён');
			} else if (updated.status === 'waiting_dns') {
				toast.info('Ожидаем делегацию NS у регистратора');
			}
		} catch (error) {
			const message = axios.isAxiosError(error)
				? (formatApiError(error.response?.data) ?? error.message)
				: 'Не удалось включить домен';
			toast.error(message);
		} finally {
			setPatching(false);
		}
	}

	async function handleCheck() {
		if (!domain) return;
		setIsChecking(true);
		try {
			const updated = await checkLandingDomain(domain.id);
			setDomain(updated);
			toast.success('Проверка DNS запущена');
		} finally {
			setIsChecking(false);
		}
	}

	if (loading) {
		return (
			<div className='flex justify-center py-16'>
				<Spinner />
			</div>
		);
	}

	if (!domain) {
		return <div className='text-center text-sm text-muted-foreground py-16'>Домен не найден</div>;
	}

	return (
		<>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4 flex'>
					<div className='flex items-start justify-between gap-3 w-full'>
						<div className='flex items-center gap-3 min-w-0'>
							<div className='rounded-lg bg-chart-1/15 p-2 shrink-0'>
								<GlobeIcon className='text-chart-1' size={20} />
							</div>
							<div className='min-w-0'>
								<CardTitle className='text-chart-1 truncate'>{domain.zoneName}</CardTitle>
								<CardDescription>Домен лендинга</CardDescription>
							</div>
						</div>
						<LandingDomainStatusBadge status={domain.status} />
					</div>
				</CardHeader>

				<CardContent className='px-4 py-4 flex flex-col gap-4'>
					{domain.errorMessage ? (
						<div className='rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
							{domain.errorMessage}
						</div>
					) : null}

					{domain.status === 'disabled' ? (
						<p className='text-xs text-muted-foreground'>
							Лендинги на этом домене недоступны (503). Sites, ссылки и статистика сохранены. Домен можно снова включить
							после настройки NS.
						</p>
					) : null}

					{domain.nsRecords && domain.nsRecords.length > 0 ? (
						<div className='flex flex-col gap-2'>
							<span className='text-sm font-medium'>NS-записи</span>
							<p className='text-xs text-muted-foreground'>Укажите эти NS у регистратора домена</p>
							{domain.nsRecords.map(ns => (
								<CopyableUrlRow key={ns} label='NS' url={ns} onCopy={copyText} showOpenLink={false} />
							))}
						</div>
					) : null}

					<LoadingWrapper show={patching || isChecking}>
						<div className='flex flex-col gap-2 items-start w-full'>
							{domain.status !== 'disabled' ? (
								<Button
									type='button'
									variant='outline'
									disabled={patching || isChecking}
									onClick={() => void handleCheck()}
								>
									<RefreshCwIcon size={14} className='mr-1' />
									Проверить DNS
								</Button>
							) : null}

							{domain.status === 'disabled' ? (
								<Button type='button' variant='outline' disabled={patching} onClick={() => void handleEnable()}>
									Включить домен
								</Button>
							) : (
								<div className='w-full border-t border-border/60 pt-3 mt-1'>
									<Button
										type='button'
										variant='destructive'
										disabled={patching}
										onClick={() => setDeactivateOpen(true)}
									>
										Отключить домен
									</Button>
								</div>
							)}
						</div>
					</LoadingWrapper>
				</CardContent>
			</Card>

			<DeactivateDomainDialog
				open={deactivateOpen}
				zoneName={domain.zoneName}
				isLoading={patching}
				onOpenChange={setDeactivateOpen}
				onConfirm={() => void handleDeactivate()}
			/>
		</>
	);
}
